import { useEffect, useState } from 'react';
import { Box, VisuallyHidden, usePrefersReducedMotion, useToken } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useRouter } from 'next/router';

const slide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
`;

/**
 * Global page-transition feedback: a thin indeterminate bar pinned to the top of the viewport, shown
 * from the moment a route change starts until it completes, errors, or is cancelled. Mounted once in
 * `_app`, so every link and router push gets feedback without per-page wiring.
 */
export const RouteChangeIndicator = () => {
  const router = useRouter();
  const [isNavigating, setIsNavigating] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [easing] = useToken('transition.easing', ['ease-in-out'], ['ease-in-out']);

  useEffect(() => {
    const start = (_url: string, { shallow }: { shallow: boolean }) => {
      // Shallow replaces (e.g. scrubbing query params) don't load a new page
      if (!shallow) setIsNavigating(true);
    };
    const stop = () => setIsNavigating(false);

    router.events.on('routeChangeStart', start);
    router.events.on('routeChangeComplete', stop);
    // Fires for both failures and cancellations (a superseded navigation), so the bar can't stick
    router.events.on('routeChangeError', stop);

    return () => {
      router.events.off('routeChangeStart', start);
      router.events.off('routeChangeComplete', stop);
      router.events.off('routeChangeError', stop);
    };
  }, [router.events]);

  return (
    <>
      <VisuallyHidden role="status" aria-live="polite">
        {isNavigating ? 'Loading page' : ''}
      </VisuallyHidden>
      {isNavigating && (
        <Box
          aria-hidden
          data-testid="route-change-indicator"
          position="fixed"
          top={0}
          left={0}
          right={0}
          h="3px"
          zIndex="toast"
          overflow="hidden"
          pointerEvents="none"
        >
          {/*
           * Reduced motion gets a static full-width bar instead of the sweep; the status region above
           * carries the announcement. Easing comes from the Verdant `transition.easing` token. Verdant's
           * durations top out at 500ms, so none fits a looping indeterminate bar: the 1.8s cycle is a
           * deliberate deviation.
           */}
          <Box
            data-testid="route-change-indicator-bar"
            h="100%"
            w={prefersReducedMotion ? '100%' : '40%'}
            bg="primary-base"
            animation={prefersReducedMotion ? undefined : `${slide} 1.8s ${easing} infinite`}
          />
        </Box>
      )}
    </>
  );
};
